/**
 * ============================================================================
 * VARIABLE REFERENCES - UTILITIES
 * ============================================================================
 *
 * Utilities for parsing, resolving, and decorating inline variable references
 * in text content. Supports the `{{ key.name }}` syntax for referencing:
 *   - Variables/Types (TypeItem -> TypeElement)
 *   - Objects/Glossary (ObjectItem -> FormFieldObject)
 *
 * Storage format: raw `{{ key.name }}` tokens in HTML content.
 * Decoration (colored chips) is applied in the editor UI only.
 *
 * @module utils/variableReferences
 */

import type { TypeItem, TypeElement } from '@/lib/stores/typesStore';
import type { ObjectItem, FormFieldObject } from '@/lib/stores/objects/types';

// ============================================================================
// TYPES
// ============================================================================

/**
 * A parsed variable reference token extracted from text content.
 */
export interface VariableReference {
  /** The full raw token, e.g. "{{ player.health }}" */
  raw: string;
  /** The object/type key, e.g. "player" */
  key: string;
  /** The field/element name, e.g. "health" */
  field: string;
  /** Start index of the token in the plain-text representation */
  startIndex: number;
  /** End index of the token in the plain-text representation */
  endIndex: number;
}

/**
 * A variable reference that has been resolved to an actual data source.
 */
export interface ResolvedReference extends VariableReference {
  /** The current resolved value (text, number, or null if unresolvable) */
  resolvedValue: string | number | null;
  /** Whether this reference came from a variable/type or an object */
  sourceType: 'variable' | 'object';
  /** The ID of the source TypeItem or ObjectItem */
  sourceId: string;
  /** The ID of the source TypeElement or FormFieldObject */
  fieldId: string;
  /** Whether the referenced field is translatable (has a string_id) */
  isTranslatable: boolean;
  /** The string_id if the referenced field is translatable */
  stringId?: string;
  /** Display label for the source (e.g. the TypeItem or ObjectItem name) */
  sourceLabel: string;
  /** Display label for the field (e.g. element name or field label) */
  fieldLabel: string;
}

/**
 * A suggestion item for the autocomplete dropdown.
 */
export interface VariableRefSuggestionItem {
  /** Unique key for React rendering */
  id: string;
  /** The reference string to insert, e.g. "player.health" */
  refString: string;
  /** Display label for the parent (type/object name) */
  parentLabel: string;
  /** Display label for the field/element */
  fieldLabel: string;
  /** Source type */
  sourceType: 'variable' | 'object';
  /** Description / tooltip */
  description?: string;
  /** Current value for preview */
  currentValue?: string | number | null;
}

// ============================================================================
// REGEX
// ============================================================================

/**
 * Matches `{{ key.field }}` tokens with flexible whitespace.
 * Supports spaces and special characters in key/field names (e.g. "language test.Text Field").
 * The key and field are separated by the FIRST dot.
 *
 * Captures:
 *   Group 0: full match including braces
 *   Group 1: key (everything before the first dot, trimmed)
 *   Group 2: field (everything after the first dot, trimmed)
 */
const VAR_REF_REGEX = /\{\{\s*([^.{}]+?)\.([^{}]+?)\s*\}\}/g;

/**
 * Matches a partial `{{` trigger at the end of text (for autocomplete detection).
 * Captures everything after `{{` up to the cursor position.
 * Supports spaces and special characters in the query.
 */
const VAR_REF_TRIGGER_REGEX = /\{\{\s*([^{}]*)$/;

// ============================================================================
// SANITISATION
// ============================================================================

/**
 * Cleans variable reference tokens in HTML content by stripping any HTML tags
 * that may have been injected inside `{{ }}` tokens (e.g. by the auto-highlight
 * system wrapping glossary matches inside a token).
 *
 * Example:
 *   Input:  `{{ <span class="text-highlight">language test</span>.Text Field }}`
 *   Output: `{{ language test.Text Field }}`
 *
 * @param html - The HTML content that may contain corrupted tokens
 * @returns HTML with clean `{{ key.field }}` tokens
 */
export function cleanVariableTokens(html: string): string {
  if (!html) return html;

  // Match {{ ... }} regions in the raw HTML string (non-greedy).
  // The content between {{ and }} may include HTML tags that need stripping.
  return html.replace(/\{\{[\s\S]*?\}\}/g, (match) => {
    // Fast path: no HTML tags inside — nothing to clean
    if (!/<[^>]+>/.test(match)) return match;

    // Strip all HTML tags, keeping only text content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = match;
    const cleanText = tempDiv.textContent || tempDiv.innerText || '';
    return cleanText;
  });
}

// ============================================================================
// PARSING
// ============================================================================

/**
 * Extracts all variable reference tokens from a text string.
 * Works on both plain text and HTML (strips tags before matching positions).
 *
 * @param text - The text content (may contain HTML)
 * @returns Array of parsed VariableReference objects
 */
export function parseVariableReferences(text: string): VariableReference[] {
  const refs: VariableReference[] = [];

  // Work with the text as-is (may contain HTML) — regex will match across it
  let match: RegExpExecArray | null;
  const regex = new RegExp(VAR_REF_REGEX.source, 'g');

  while ((match = regex.exec(text)) !== null) {
    refs.push({
      raw: match[0],
      key: match[1].trim(),
      field: match[2].trim(),
      startIndex: match.index,
      endIndex: match.index + match[0].length,
    });
  }

  return refs;
}

/**
 * Detects whether the user is currently typing a variable reference trigger.
 * Call this with the text before the cursor to determine if the autocomplete
 * should open.
 *
 * @param textBeforeCursor - Text content before the cursor position
 * @returns An object with `isTriggered` and optional `query` (partial input after `{{`)
 */
export function detectVariableRefTrigger(
  textBeforeCursor: string
): { isTriggered: boolean; query: string } {
  const match = VAR_REF_TRIGGER_REGEX.exec(textBeforeCursor);
  if (match) {
    return { isTriggered: true, query: match[1] || '' };
  }
  return { isTriggered: false, query: '' };
}

// ============================================================================
// RESOLVING
// ============================================================================

/**
 * Resolves a single variable reference against available types and objects.
 * Matching is case-insensitive on both key and field.
 *
 * @param ref - The parsed variable reference
 * @param types - Available TypeItems from the typesStore
 * @param objects - Available ObjectItems from the objectsStore
 * @returns A ResolvedReference, or null if no match was found
 */
export function resolveReference(
  ref: VariableReference,
  types: TypeItem[],
  objects: ObjectItem[]
): ResolvedReference | null {
  const keyLower = ref.key.toLowerCase();
  const fieldLower = ref.field.toLowerCase();

  // 1. Try to match against variables/types
  for (const typeItem of types) {
    if (typeItem.name.toLowerCase() === keyLower) {
      const element = typeItem.data?.find(
        (el: TypeElement) => el.name.toLowerCase() === fieldLower
      );
      if (element) {
        return {
          ...ref,
          resolvedValue: getTypeElementValue(element),
          sourceType: 'variable',
          sourceId: typeItem.id,
          fieldId: element.id,
          isTranslatable: false, // TypeElements are not translatable
          sourceLabel: typeItem.name,
          fieldLabel: element.name,
        };
      }
    }
  }

  // 2. Try to match against objects/glossary
  for (const objectItem of objects) {
    if (objectItem.name.toLowerCase() === keyLower) {
      // Search through all sections and fields
      for (const section of objectItem.data || []) {
        for (const field of section.fields || []) {
          if (field.label.toLowerCase() === fieldLower) {
            const stringId = getFieldStringId(field);
            return {
              ...ref,
              resolvedValue: getFieldDisplayValue(field),
              sourceType: 'object',
              sourceId: objectItem.id,
              fieldId: field.id,
              isTranslatable: field.translation && !!stringId,
              stringId: stringId || undefined,
              sourceLabel: objectItem.name,
              fieldLabel: field.label,
            };
          }
        }
      }
    }
  }

  return null;
}

/**
 * Resolves all variable references in a text against available data.
 *
 * @param text - The text content to scan
 * @param types - Available TypeItems
 * @param objects - Available ObjectItems
 * @returns Array of ResolvedReferences (only successfully resolved ones)
 */
export function resolveAllReferences(
  text: string,
  types: TypeItem[],
  objects: ObjectItem[]
): ResolvedReference[] {
  const parsed = parseVariableReferences(text);
  const resolved: ResolvedReference[] = [];

  for (const ref of parsed) {
    const result = resolveReference(ref, types, objects);
    if (result) {
      resolved.push(result);
    }
  }

  return resolved;
}

// ============================================================================
// DECORATION
// ============================================================================

/**
 * Wraps `{{ key.name }}` tokens in the given HTML string with styled spans
 * for visual display in the editor. The span displays the **resolved value**
 * (e.g. "John") so users see the actual content inline. The raw token is
 * preserved in data attributes for storage/export recovery.
 *
 * Each span includes data attributes for identification:
 *   - `data-ref-type`: 'variable' or 'object'
 *   - `data-ref-key`: the key part
 *   - `data-ref-field`: the field part
 *   - `data-ref-value`: the resolved value (if available)
 *   - `title`: tooltip showing the reference path (key.field)
 *
 * @param html - The raw HTML content containing `{{ }}` tokens
 * @param resolvedRefs - Pre-resolved references (for value display)
 * @returns HTML string with decorated reference spans
 */
export function decorateReferencesInHTML(
  html: string,
  resolvedRefs: ResolvedReference[]
): string {
  // Build a lookup map from raw token -> resolved reference
  const refMap = new Map<string, ResolvedReference>();
  for (const ref of resolvedRefs) {
    refMap.set(ref.raw, ref);
  }

  // Helper to escape a string for use inside an HTML attribute
  const escapeAttr = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Replace tokens that are NOT already inside a .var-ref span
  return html.replace(VAR_REF_REGEX, (match, rawKey, rawField) => {
    const key = rawKey.trim();
    const field = rawField.trim();
    const resolved = refMap.get(match);
    const refType = resolved?.sourceType || 'unknown';
    const refValue = resolved?.resolvedValue ?? '';
    const isUnresolved = !resolved;

    // Display: show the resolved value; fall back to key.field for unresolved
    const displayText = isUnresolved
      ? `${key}.${field}`
      : String(refValue) || `${key}.${field}`;

    // Tooltip: show the reference path so users know where it comes from
    const tooltip = resolved
      ? `{{ ${resolved.sourceLabel}.${resolved.fieldLabel} }}`
      : `{{ ${key}.${field} }} (unresolved)`;

    return (
      `<span class="var-ref${isUnresolved ? ' var-ref-unresolved' : ''}" ` +
      `data-ref-type="${escapeAttr(refType)}" ` +
      `data-ref-key="${escapeAttr(key)}" ` +
      `data-ref-field="${escapeAttr(field)}" ` +
      `data-ref-value="${escapeAttr(String(refValue))}" ` +
      `title="${escapeAttr(tooltip)}" ` +
      `contenteditable="false">` +
      `${displayText}` +
      `</span>`
    );
  });
}

/**
 * Strips variable reference decoration spans from HTML, leaving only
 * the raw `{{ key.name }}` tokens. Use this before saving content.
 *
 * @param html - The decorated HTML content
 * @returns Clean HTML with raw tokens only
 */
export function stripReferenceDecoration(html: string): string {
  if (!html) return html;

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  const refSpans = tempDiv.querySelectorAll('.var-ref');
  refSpans.forEach((span) => {
    const key = span.getAttribute('data-ref-key') || '';
    const field = span.getAttribute('data-ref-field') || '';
    const rawToken = `{{ ${key}.${field} }}`;
    const textNode = document.createTextNode(rawToken);
    span.parentNode?.replaceChild(textNode, span);
  });

  return tempDiv.innerHTML;
}

// ============================================================================
// SUGGESTION BUILDING
// ============================================================================

/**
 * Builds a list of suggestion items for the autocomplete dropdown,
 * combining variables/types and objects/glossary.
 *
 * @param types - Available TypeItems from the typesStore
 * @param objects - Available ObjectItems from the objectsStore
 * @param query - Optional filter query (supports "key.field" or just "key" or "field")
 * @returns Filtered and sorted suggestion items
 */
export function buildSuggestionItems(
  types: TypeItem[],
  objects: ObjectItem[],
  query: string = ''
): VariableRefSuggestionItem[] {
  const items: VariableRefSuggestionItem[] = [];
  const queryLower = query.toLowerCase().trim();
  const [queryKey, queryField] = queryLower.includes('.')
    ? queryLower.split('.', 2)
    : [queryLower, ''];

  // Add variable/type suggestions
  for (const typeItem of types) {
    if (!typeItem.data?.length) continue;

    for (const element of typeItem.data) {
      const refString = `${typeItem.name}.${element.name}`;

      // Filter by query
      if (queryLower) {
        const matchesKey = typeItem.name.toLowerCase().includes(queryKey);
        const matchesField = queryField
          ? element.name.toLowerCase().includes(queryField)
          : true;
        const matchesFull = refString.toLowerCase().includes(queryLower);

        if (!matchesFull && !(matchesKey && matchesField)) continue;
      }

      items.push({
        id: `var_${typeItem.id}_${element.id}`,
        refString,
        parentLabel: typeItem.name,
        fieldLabel: element.name,
        sourceType: 'variable',
        description: element.description || typeItem.description,
        currentValue: getTypeElementValue(element),
      });
    }
  }

  // Add object/glossary suggestions
  for (const objectItem of objects) {
    if (!objectItem.data?.length) continue;

    for (const section of objectItem.data) {
      for (const field of section.fields) {
        const refString = `${objectItem.name}.${field.label}`;

        // Filter by query
        if (queryLower) {
          const matchesKey = objectItem.name.toLowerCase().includes(queryKey);
          const matchesField = queryField
            ? field.label.toLowerCase().includes(queryField)
            : true;
          const matchesFull = refString.toLowerCase().includes(queryLower);

          if (!matchesFull && !(matchesKey && matchesField)) continue;
        }

        items.push({
          id: `obj_${objectItem.id}_${field.id}`,
          refString,
          parentLabel: objectItem.name,
          fieldLabel: field.label,
          sourceType: 'object',
          description: objectItem.description,
          currentValue: getFieldDisplayValue(field),
        });
      }
    }
  }

  // Sort: variables first, then objects; alphabetical within each group
  items.sort((a, b) => {
    if (a.sourceType !== b.sourceType) {
      return a.sourceType === 'variable' ? -1 : 1;
    }
    return a.refString.localeCompare(b.refString);
  });

  return items;
}

// ============================================================================
// EXPORT HELPERS
// ============================================================================

/**
 * Supported export platforms for variable reference mapping.
 */
export type ExportPlatform = 'unity' | 'roblox' | 'json' | 'godot' | 'generic';

/**
 * Maps `{{ key.name }}` tokens in a string to engine-specific interpolation
 * syntax for code generation/export.
 *
 * Platform mappings:
 *   - **unity**:   `{key.name}`   (C# string interpolation style)
 *   - **roblox**:  `" .. Variables.Key.Name .. "` (Lua concatenation)
 *   - **godot**:   `{key.name}`   (GDScript format string)
 *   - **json**:    `${key.name}`  (Template literal style)
 *   - **generic**: `{key.name}`   (Simple brace syntax)
 *
 * @param text - The text containing `{{ key.name }}` tokens
 * @param platform - Target export platform
 * @param namingFn - Optional function to convert key/field names to the target naming convention
 * @returns Text with tokens replaced by platform-specific syntax
 */
export function mapReferencesForExport(
  text: string,
  platform: ExportPlatform,
  namingFn?: (name: string) => string
): string {
  const applyNaming = namingFn || ((name: string) => name);

  return text.replace(VAR_REF_REGEX, (_match, rawKey: string, rawField: string) => {
    const mappedKey = applyNaming(rawKey.trim());
    const mappedField = applyNaming(rawField.trim());

    switch (platform) {
      case 'unity':
        // C# string interpolation: {Variables.Player.Health}
        return `{${mappedKey}.${mappedField}}`;

      case 'roblox':
        // Lua concatenation: " .. Variables.Player.Health .. "
        return `" .. Variables.${mappedKey}.${mappedField} .. "`;

      case 'godot':
        // GDScript format: {key.field}
        return `{${mappedKey}.${mappedField}}`;

      case 'json':
        // JSON template literal: ${key.field}
        return `\${${mappedKey}.${mappedField}}`;

      case 'generic':
      default:
        // Simple brace syntax
        return `{${mappedKey}.${mappedField}}`;
    }
  });
}

/**
 * Strips `{{ key.name }}` tokens from text entirely, replacing with just the
 * field name. Useful when you want plain text without interpolation syntax.
 *
 * @param text - Text with variable reference tokens
 * @returns Plain text with tokens replaced by field names
 */
export function stripReferencesToPlainText(text: string): string {
  return text.replace(VAR_REF_REGEX, (_match, _key: string, field: string) => field.trim());
}

// ============================================================================
// VALUE EXTRACTION HELPERS
// ============================================================================

/**
 * Extracts the display value from a TypeElement.
 */
function getTypeElementValue(element: TypeElement): string | number | null {
  if (element.valueType === 'single' && element.numericValue != null) {
    return element.numericValue;
  }
  if (element.valueType === 'range') {
    if (element.rangeMin != null && element.rangeMax != null) {
      return `${element.rangeMin}-${element.rangeMax}`;
    }
  }
  return element.description || element.name;
}

/**
 * Extracts the display value from a FormFieldObject.
 */
function getFieldDisplayValue(field: FormFieldObject): string | number | null {
  const val = field.value;

  if (val == null || val === '') return null;

  // TranslatableValue
  if (typeof val === 'object' && !Array.isArray(val) && 'text' in val) {
    return (val as { text: string }).text || null;
  }

  // TranslatableValue[]
  if (Array.isArray(val)) {
    const texts = val
      .filter((v): v is { text: string } => typeof v === 'object' && v !== null && 'text' in v)
      .map((v) => v.text);
    if (texts.length) return texts.join(', ');
    // String array
    const strings = val.filter((v): v is string => typeof v === 'string');
    if (strings.length) return strings.join(', ');
  }

  if (typeof val === 'string') return val;
  if (typeof val === 'number') return val;
  if (typeof val === 'boolean') return val ? 'true' : 'false';

  return String(val);
}

/**
 * Extracts the string_id from a FormFieldObject (handles both legacy and new format).
 */
function getFieldStringId(field: FormFieldObject): string | null {
  // Legacy format: string_id directly on the field
  if (field.string_id) return field.string_id;

  // New TranslatableValue format
  const val = field.value;
  if (typeof val === 'object' && !Array.isArray(val) && val !== null && 'string_id' in val) {
    return (val as { string_id?: string }).string_id || null;
  }

  return null;
}
